/**
 * Alert Helpers
 *
 * Utility functions for working with alerts
 * Includes filtering, sorting, formatting, and categorization
 */
import { formatDateShort } from './dateHelpers';

/**
 * Filter alerts by various criteria
 * @param {Array} alerts - Array of alert objects
 * @param {Object} filters - Filter criteria
 * @returns {Array} - Filtered alerts
 */
export function filterAlerts(alerts, filters = {}) {
  if (!alerts || !Array.isArray(alerts)) return [];

  let filtered = [...alerts];

  // Filter by severity
  if (filters.severity && filters.severity !== 'all') {
    filtered = filtered.filter(alert => alert.severity === filters.severity);
  }

  // Filter by category
  if (filters.category && filters.category !== 'all') {
    filtered = filtered.filter(alert => alert.category === filters.category);
  }

  // Filter by read/unread
  if (filters.readStatus === 'unread') {
    filtered = filtered.filter(alert => !alert.read);
  } else if (filters.readStatus === 'read') {
    filtered = filtered.filter(alert => alert.read);
  }

  // Filter by dismissed
  if (filters.hideDismissed) {
    filtered = filtered.filter(alert => !alert.dismissed);
  }

  // Filter by action required
  if (filters.actionRequired) {
    filtered = filtered.filter(alert => alert.actionRequired);
  }

  return filtered;
}

/**
 * Sort alerts by various criteria
 * @param {Array} alerts - Array of alert objects
 * @param {string} sortBy - Sort field
 * @param {string} sortOrder - 'asc' or 'desc'
 * @returns {Array} - Sorted alerts
 */
export function sortAlerts(alerts, sortBy = 'createdAt', sortOrder = 'desc') {
  if (!alerts || !Array.isArray(alerts)) return [];

  return [...alerts].sort((a, b) => {
    let aVal, bVal;

    switch (sortBy) {
      case 'createdAt':
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        break;

      case 'severity':
        // Order: critical > warning > info
        const severityOrder = { critical: 3, warning: 2, info: 1 };
        aVal = severityOrder[a.severity] || 0;
        bVal = severityOrder[b.severity] || 0;
        break;

      case 'title':
        aVal = (a.title || '').toLowerCase();
        bVal = (b.title || '').toLowerCase();
        break;

      default:
        aVal = a[sortBy];
        bVal = b[sortBy];
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });
}

/**
 * Group alerts by category
 * @param {Array} alerts - Array of alert objects
 * @returns {Object} - Alerts grouped by category
 */
export function groupAlertsByCategory(alerts) {
  if (!alerts || !Array.isArray(alerts)) return {};

  return alerts.reduce((groups, alert) => {
    const category = alert.category || 'other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(alert);
    return groups;
  }, {});
}

/**
 * Group alerts by severity
 * @param {Array} alerts - Array of alert objects
 * @returns {Object} - Alerts grouped by severity
 */
export function groupAlertsBySeverity(alerts) {
  if (!alerts || !Array.isArray(alerts)) return { critical: [], warning: [], info: [] };

  return alerts.reduce((groups, alert) => {
    const severity = alert.severity || 'info';
    if (!groups[severity]) {
      groups[severity] = [];
    }
    groups[severity].push(alert);
    return groups;
  }, { critical: [], warning: [], info: [] });
}

/**
 * Get severity color for UI display
 * @param {string} severity - Alert severity
 * @returns {string} - CSS color value
 */
export function getSeverityColor(severity) {
  const colors = {
    critical: 'var(--ios-red, #FF3B30)',
    warning: 'var(--ios-orange, #FF9500)',
    info: 'var(--ios-blue, #007AFF)',
    success: 'var(--ios-green, #34C759)'
  };

  return colors[severity] || colors.info;
}

/**
 * Get severity icon
 * @param {string} severity - Alert severity
 * @returns {string} - Icon emoji or character
 */
export function getSeverityIcon(severity) {
  const icons = {
    critical: '🚨',
    warning: '⚠️',
    info: 'ℹ️',
    success: '✅'
  };

  return icons[severity] || icons.info;
}

/**
 * Get category display name
 * @param {string} category - Alert category
 * @returns {string} - Human-readable category name
 */
export function getCategoryDisplayName(category) {
  const names = {
    connection: 'Conexión',
    billing: 'Facturación',
    usage: 'Uso',
    system: 'Sistema',
    info: 'Información',
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    messenger: 'Messenger',
    calendar: 'Calendario',
    ecommerce: 'E-commerce',
    bot: 'Bot'
  };

  return names[category] || category;
}

/**
 * Format relative time (e.g., "hace 5 minutos")
 * @param {Date} date - Date object
 * @returns {string} - Formatted relative time
 */
export function formatRelativeTime(date, t = null) {
  if (!date) return '';

  const now = new Date();
  const diffMs = now - new Date(date);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // `t` opcional: quien lo pase obtiene el texto traducido; quien no, el
  // castellano de siempre. Una sola definición del cálculo — duplicarla para
  // tener versión i18n es exactamente cómo se desincronizan dos relojes.
  if (t) {
    if (diffMins < 1) return t('relativeTime.now');
    if (diffMins < 60) return t('relativeTime.minutes', { count: diffMins });
    if (diffHours < 24) return t('relativeTime.hours', { count: diffHours });
    if (diffDays < 7) return t('relativeTime.days', { count: diffDays });
    return formatDateShort(date);
  }

  if (diffMins < 1) return 'Justo ahora';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;

  // Format as date
  return formatDateShort(date);
}

/**
 * Check if alert is expired
 * @param {Object} alert - Alert object
 * @returns {boolean} - True if expired
 */
export function isAlertExpired(alert) {
  if (!alert || !alert.expiresAt) return false;

  const now = new Date();
  const expiresAt = new Date(alert.expiresAt);

  return now > expiresAt;
}

/**
 * Get unread alert count
 * @param {Array} alerts - Array of alert objects
 * @returns {number} - Count of unread alerts
 */
export function getUnreadCount(alerts) {
  if (!alerts || !Array.isArray(alerts)) return 0;
  return alerts.filter(alert => !alert.read && !alert.dismissed).length;
}

/**
 * Get critical alert count
 * @param {Array} alerts - Array of alert objects
 * @returns {number} - Count of critical alerts
 */
export function getCriticalCount(alerts) {
  if (!alerts || !Array.isArray(alerts)) return 0;
  return alerts.filter(alert => alert.severity === 'critical' && !alert.dismissed).length;
}

/**
 * Get alert priority score (for sorting by importance)
 * @param {Object} alert - Alert object
 * @returns {number} - Priority score (higher = more important)
 */
export function getAlertPriority(alert) {
  let score = 0;

  // Severity score
  const severityScores = {
    critical: 100,
    warning: 50,
    info: 10
  };
  score += severityScores[alert.severity] || 0;

  // Unread bonus
  if (!alert.read) score += 20;

  // Action required bonus
  if (alert.actionRequired) score += 30;

  // Recency bonus (newer = higher priority)
  if (alert.createdAt) {
    const ageHours = (new Date() - new Date(alert.createdAt)) / 3600000;
    if (ageHours < 1) score += 15;
    else if (ageHours < 24) score += 10;
    else if (ageHours < 168) score += 5; // 1 week
  }

  return score;
}

/**
 * Sort alerts by priority
 * @param {Array} alerts - Array of alert objects
 * @returns {Array} - Alerts sorted by priority (highest first)
 */
export function sortByPriority(alerts) {
  if (!alerts || !Array.isArray(alerts)) return [];

  return [...alerts].sort((a, b) => {
    return getAlertPriority(b) - getAlertPriority(a);
  });
}

export default {
  filterAlerts,
  sortAlerts,
  groupAlertsByCategory,
  groupAlertsBySeverity,
  getSeverityColor,
  getSeverityIcon,
  getCategoryDisplayName,
  formatRelativeTime,
  isAlertExpired,
  getUnreadCount,
  getCriticalCount,
  getAlertPriority,
  sortByPriority
};
